import React, { useEffect, useRef, useState } from 'react';
// Adjust path if your Button/ButtonGroup components are elsewhere
import { Button, ButtonGroup } from '../../buttons/Buttons';

// Import the style definitions (adjust path as necessary)
import { colors, typography, layout, commonStyles } from '../../about/News/styles/minimalStyles'; // Adjust path accordingly

const SpectralLibrarySection = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);
  const videoRef = useRef(null);

  const [isPreviewMode, setIsPreviewMode] = useState(true);
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);

  // --- INLINE STYLES ---
  const styles = {
    sectionContainerBase: {
      padding: '4rem 2rem',
      maxWidth: layout.maxWidth,
      margin: '60px auto', // Added top/bottom margin
      backgroundColor: colors.darkBlue || '#111d4a', // Define darkBlue in minimalStyles or use fallback
      borderRadius: '12px', // Softer edges
      color: colors.white,
      opacity: 0,
      transform: 'translateY(20px)',
      transition: 'opacity 0.8s ease-out, transform 0.8s ease-out',
      willChange: 'opacity, transform',
      overflow: 'hidden', // Contain child elements
    },
    sectionContainerVisible: {
      opacity: 1,
      transform: 'translateY(0)',
    },
    contentWrapper: {
      textAlign: 'center',
      marginBottom: '3rem', // Increased spacing
    },
    mainTitle: {
      fontSize: typography.largeTitleSize, // Use defined size
      fontWeight: typography.boldWeight, // Make title bolder
      marginBottom: '1rem',
      color: colors.white,
    },
    description: {
      fontSize: '1.1rem', // Consistent size
      maxWidth: '650px', // Slightly wider
      margin: '0 auto',
      color: 'rgba(255, 255, 255, 0.9)',
      lineHeight: 1.6,
    },
    imageShowcase: {
      marginBottom: '4rem', // Increased spacing
      borderRadius: '8px',
      overflow: 'hidden',
      boxShadow: '0 10px 30px rgba(0, 0, 0, 0.3)', // Softer shadow
      position: 'relative',
      backgroundColor: '#000', // BG for video loading
    },
    showcaseVideo: {
      width: '100%',
      height: 'auto',
      display: 'block',
      filter: isPreviewMode ? 'brightness(0.9)' : 'none', // Example filter
      transition: 'filter 0.3s ease',
    },
    videoControlButtonBase: {
      position: 'absolute',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      width: '72px',
      height: '72px',
      borderRadius: '50%',
      backgroundColor: `${colors.accent || '#007bff'}CC`, // Use accent color with alpha
      color: colors.white,
      border: 'none',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      transition: 'all 0.3s ease',
      opacity: 0.9,
      boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)',
      zIndex: 2,
      outline: 'none',
    },
    videoControlButtonHover: {
       backgroundColor: `${colors.accent || '#007bff'}`, // Solid accent on hover
       transform: 'translate(-50%, -50%) scale(1.05)',
       opacity: 1,
    },
    videoControlIcon: { // Style the SVG container if needed
      width: '28px',
      height: '28px',
    },
    featuresSection: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', // Responsive grid
      gap: '2rem', // Use defined gap
      marginBottom: '3rem', // Spacing below features
    },
    featureItemBase: {
      textAlign: 'center',
      padding: '1.5rem', // Adjust padding
      transition: 'transform 0.3s ease, box-shadow 0.3s ease, background-color 0.3s ease',
      borderRadius: '8px',
      backgroundColor: 'transparent', // Start transparent
       // Simplified inline animation based on visibility
       opacity: isVisible ? 1 : 0,
       transform: isVisible ? 'translateY(0)' : 'translateY(15px)',
       // Note: Staggered delay is complex inline, apply base transition here
       transitionProperty: 'opacity, transform, background-color, box-shadow',
       transitionDuration: '0.6s, 0.6s, 0.3s, 0.3s', // Duration for each property
       transitionTimingFunction: 'ease-out',
    },
    featureItemHover: {
      transform: 'translateY(-5px)',
      boxShadow: '0 10px 20px rgba(0, 0, 0, 0.15)',
      backgroundColor: 'rgba(255, 255, 255, 0.05)',
    },
    featureIcon: {
      margin: '0 auto 1rem',
      width: '48px',
      height: '48px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: colors.lightBlue || '#a5b4fc', // Define lightBlue or fallback (e.g., light indigo)
    },
    featureTitle: {
      fontSize: '1.25rem',
      fontWeight: typography.boldWeight, // Bolder feature titles
      marginBottom: '0.75rem',
      color: colors.white,
    },
    featureDescription: {
      fontSize: '1rem',
      color: 'rgba(255, 255, 255, 0.85)', // Slightly less transparent
      lineHeight: 1.5,
    },
    ctaButtons: {
      display: 'flex',
      justifyContent: 'center',
      gap: layout.gap, // Consistent gap
      marginTop: '1rem', // Adjusted spacing
       // Simplified inline animation based on visibility
       opacity: isVisible ? 1 : 0,
       transform: isVisible ? 'translateY(0)' : 'translateY(15px)',
       transition: 'opacity 0.6s ease-out 0.8s, transform 0.6s ease-out 0.8s', // Apply delay
    },
     // Define styles for your Button components IF they don't accept style prop well
     // Otherwise, pass style prop directly to Button component instance
     primaryButtonStyle: {
         backgroundColor: colors.white,
         color: colors.darkBlue || '#111d4a',
         padding: '0.75rem 1.5rem',
         borderRadius: '2rem', // Pill shape
         fontWeight: typography.boldWeight,
         textDecoration: 'none',
         border: 'none',
         // Add base transition from Button component if needed
     },
     secondaryButtonStyle: {
         backgroundColor: 'transparent',
         color: colors.white,
         padding: '0.75rem 1.5rem',
         borderRadius: '2rem', // Pill shape
         fontWeight: typography.boldWeight,
         textDecoration: 'none',
         border: `1px solid ${colors.white}80`, // White border with alpha
         // Add base transition from Button component if needed
     },
     // Hover styles assumed handled by Button component or need state toggle here
  };
  // --- END INLINE STYLES ---

  // State for hover effects on features and buttons
  const [hoveredFeature, setHoveredFeature] = useState(null);
  const [hoveredCTA, setHoveredCTA] = useState(null); // 'primary' or 'secondary'
  const [hoveredVideoButton, setHoveredVideoButton] = useState(false);


  useEffect(() => {
    // Simplified visibility detection
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(entry.target); // Observe only once

           // Start preview mode when section becomes visible
           if (videoRef.current && isPreviewMode) {
                videoRef.current.currentTime = 0;
                videoRef.current.muted = true;
                videoRef.current.playbackRate = 1; // Slower preview
                videoRef.current.play().catch(e => {
                  // Autoplay likely prevented, user interaction needed
                  // // console.log'Preview autoplay prevented:', e);
                });
            }
        }
      },
      { threshold: 0.15 } // Trigger when 15% is visible
    );

    const currentSectionRef = sectionRef.current; // Capture ref value
    if (currentSectionRef) {
      observer.observe(currentSectionRef);
    }

     // Update video playing state on play/pause events
    const videoElement = videoRef.current; // Capture ref value
    const handlePlay = () => setIsVideoPlaying(true);
    const handlePause = () => setIsVideoPlaying(false);

    if (videoElement) {
      videoElement.addEventListener('play', handlePlay);
      videoElement.addEventListener('pause', handlePause);
    }

    return () => {
        if (currentSectionRef) {
            observer.unobserve(currentSectionRef);
        }
         if (videoElement) {
            videoElement.removeEventListener('play', handlePlay);
            videoElement.removeEventListener('pause', handlePause);
         }
    };
  }, [isPreviewMode]); // Re-run effect if preview mode changes

  const handleGetStarted = () => { window.location.href = "/sign-in"; };
  const handleExplore = () => { window.location.href = "/spectral-library"; }; // Link to library page

  const handleVideoToggle = () => {
    if (videoRef.current) {
      if (videoRef.current.paused || isPreviewMode) {
        if (isPreviewMode) {
          setIsPreviewMode(false); // Exit preview mode
          videoRef.current.currentTime = 0;
          videoRef.current.muted = false;
          videoRef.current.playbackRate = 1.0; // Normal speed
        }
        videoRef.current.play().catch(e => console.error("Play error:", e));
      } else {
        videoRef.current.pause();
      }
    }
  };

  // Combine base and visible styles for the container
  const sectionContainerStyle = isVisible
    ? { ...styles.sectionContainerBase, ...styles.sectionContainerVisible }
    : styles.sectionContainerBase;

  // Define feature data including icons
    const features = [
        {
            icon: ( // Placeholder Icon 1
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 2L2 7L12 12L22 7L12 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M2 17L12 22L22 17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M2 12L12 17L22 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
            ),
            title: "Advanced Spectral Analysis",
            description: "Identify the chemical makeup of your samples with precision using AI-powered algorithms"
        },
        {
            icon: ( // Placeholder Icon 2
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <rect x="3" y="3" width="18" height="18" rx="2" ry="2" stroke="currentColor" strokeWidth="2"/>
                    <line x1="9" y1="3" x2="9" y2="21" stroke="currentColor" strokeWidth="2"/>
                    <line x1="15" y1="3" x2="15" y2="21" stroke="currentColor" strokeWidth="2"/>
                    <line x1="3" y1="9" x2="21" y2="9" stroke="currentColor" strokeWidth="2"/>
                    <line x1="3" y1="15" x2="21" y2="15" stroke="currentColor" strokeWidth="2"/>
                </svg>
            ),
            title: "Multi-method Support",
            description: "Process both FTIR and Raman spectra in a single, unified platform"
        },
        {
            icon: ( // Placeholder Icon 3
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M13 2L3 14H12L11 22L21 10H12L13 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
            ),
            title: "Real-time Insights",
            description: "Get instant, accurate results to make informed decisions about your samples"
        }
    ];


  return (
    <div style={sectionContainerStyle} ref={sectionRef}>
      <div style={styles.contentWrapper}>
        <h1 style={styles.mainTitle}>Plastic Analytics' Spectral Library</h1>
        <p style={styles.description}>
          AI that helps you analyse, identify, and understand chemical composition at scale
        </p>
      </div>

      <div style={styles.imageShowcase}>
        <video
          ref={videoRef}
          src="https://firebasestorage.googleapis.com/v0/b/plasticanalytics-ada31.appspot.com/o/landingPage%2FpromotionalVideo%2Fpromo-video.mp4?alt=media&token=8a2a57d2-7f94-4b24-a328-a8e026efd0ca"
          // poster="path/to/your/video-poster.jpg" // Recommended
          style={styles.showcaseVideo}
          playsInline
          loop
          preload="metadata"
          muted={isPreviewMode} // Muted state controlled by isPreviewMode
        />
        <button
           style={hoveredVideoButton ? {...styles.videoControlButtonBase, ...styles.videoControlButtonHover} : styles.videoControlButtonBase}
           onClick={handleVideoToggle}
           onMouseEnter={() => setHoveredVideoButton(true)}
           onMouseLeave={() => setHoveredVideoButton(null)}
           aria-label={!isVideoPlaying || isPreviewMode ? "Play video" : "Pause video"}
        >
          {!isVideoPlaying || isPreviewMode ? (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg" style={styles.videoControlIcon}>
              <path d="M8 5V19L19 12L8 5Z" />
            </svg>
          ) : (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg" style={styles.videoControlIcon}>
              <path d="M6 4H10V20H6V4Z" />
              <path d="M14 4H18V20H14V4Z" />
            </svg>
          )}
        </button>
      </div>

      <div style={styles.featuresSection}>
         {features.map((feature, index) => {
            // Calculate transition delay for staggered effect (complex inline)
            const delay = isVisible ? 0.5 + index * 0.1 : 0;
            const featureItemStyle = {
                ...styles.featureItemBase,
                transitionDelay: `${delay}s`, // Apply calculated delay
                ...(hoveredFeature === index ? styles.featureItemHover : {}) // Apply hover style
            };

            return (
              <div
                key={index}
                style={featureItemStyle}
                onMouseEnter={() => setHoveredFeature(index)}
                onMouseLeave={() => setHoveredFeature(null)}
              >
                <div style={styles.featureIcon}>
                  {feature.icon}
                </div>
                <h3 style={styles.featureTitle}>{feature.title}</h3>
                <p style={styles.featureDescription}>{feature.description}</p>
              </div>
            );
         })}
      </div>

      {/* Assuming ButtonGroup accepts a style prop for layout */}
      <div style={styles.ctaButtons}>
          {/* Pass inline styles directly to your Button components */}
          <Button
            type="primary"
            style={styles.primaryButtonStyle} // You might need base + specific styles combined
            onClick={handleGetStarted}
            // Add hover state handling if Button doesn't do it internally
            onMouseEnter={() => setHoveredCTA('primary')}
            onMouseLeave={() => setHoveredCTA(null)}
           >
              Get started
          </Button>
          <Button
            type="secondary"
            style={styles.secondaryButtonStyle} // You might need base + specific styles combined
            onClick={handleExplore}
            onMouseEnter={() => setHoveredCTA('secondary')}
            onMouseLeave={() => setHoveredCTA(null)}
           >
              Explore
          </Button>
      </div>
    </div>
  );
};

export default SpectralLibrarySection;